// GlobalAgentButton.jsx — the GLOBAL floating command-agent surface, mounted once
// in App.jsx next to <WorldSwitcher/> so it rides on EVERY staff screen (both the
// OPD and IPD/ward worlds). It is the everywhere sibling of the consult-only
// CommandAgent and the ward bed's command-dock: ask, in voice or text, to do
// anything you could do by clicking — navigate, open a patient, apply a protocol,
// create a task, send a message, book an appointment, star a favourite — bounded
// to your own access (askGlobalAgent runs the native `$ai` loop AS YOU).
//
// It reuses the Calm-Clinical visual language of CommandAgent verbatim (a draggable
// portal button, the translucent anchored panel, the `window.OPDVoice` Deepgram
// wiring, the staged-chip → Confirm grammar) — no restyle. NOTHING runs until the
// user confirms; on confirm each kept action executes client-side via executeAction
// (SPA-navigate / a run-as-user FHIR write / savePrefs). When `$ai` is not enabled
// on the project it shows the SAME honest message Spaces does (fail loud).
import React from 'react';
import { createPortal } from 'react-dom';
import {
  Sparkles, Mic, Square, X, Check, ArrowRight, User, Puzzle,
  Mail, CalendarDays, Star, Plus, Paperclip, Camera,
} from 'lucide-react';
import { getMedplum, deriveCurrentUser } from './medplum-client.js';
import { askGlobalAgent, executeAction, taskActionSignature } from './global-agent.js';
import { attachWardPhoto, resizeImageFile, understandPhoto, fieldLines } from '../components/ipd/QuickPhoto.jsx';
import { cn } from './utils.js';
import {
  clearRouteAgentSession, nextRouteAgentTurnId, routeAgentFieldIsFresh,
  routeAgentSessionKey, useRouteAgentField,
  recordAgentSessionTask, readAgentSessionTasks,
} from './route-agent-session.js';
import { Button } from '@/components/ui/button.jsx';
import { Card } from '@/components/ui/card.jsx';
import { Kbd } from '@/components/ui/kbd.jsx';
import { Spinner } from '@/components/ui/spinner.jsx';
import {
  InputGroup, InputGroupTextarea, InputGroupAddon, InputGroupButton, InputGroupText,
} from '@/components/ui/input-group.jsx';
import { Message, MessageContent } from '@/components/ui/message.jsx';
import { Bubble, BubbleContent } from '@/components/ui/bubble.jsx';
import {
  MessageScrollerProvider, MessageScroller, MessageScrollerViewport,
  MessageScrollerContent, MessageScrollerItem, MessageScrollerButton,
} from '@/components/ui/message-scroller.jsx';

// The panel is now built on the shadcn design system (Card/Button/Textarea/Badge)
// with theme-aware tokens (hsl(var(--*))), so it renders correctly in light AND
// dark and picks up the ward-indigo primary in the IPD world — the old
// Calm-Clinical --opd-* token set was light-only.

// Persist the button position separately from the consult CommandAgent's so the two
// never share a spot when both are around (they never render together, but keep the
// stored positions distinct anyway).
const POS_KEY = 'healther.opd.globalagentbtn';
const loadPos = () => { try { return JSON.parse(localStorage.getItem(POS_KEY)) || null; } catch (_) { return null; } };
const savePos = (p) => { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch (_) {} };

// A one-line human label per staged action type for the chip.
function actionLabel(a) {
  switch (a.type) {
    case 'navigate_to': return a.description || `Go to ${a.label || a.path}`;
    case 'open_patient': return a.description || `Open ${a.patientName || 'patient'}${a.mode === 'consult' ? ' (consult)' : ''}`;
    case 'apply_protocol': return a.description || 'Apply protocol';
    case 'create_task': return a.description || `Create task: ${a.title}`;
    case 'send_message': return a.description || 'Send message';
    case 'book_appointment': return a.description || 'Book appointment';
    case 'star_favorite': return a.description || `Star ${a.kind}: ${a.value}`;
    case 'attach_photo': return a.description || `Attach photo to ${a.patientName || 'patient'}`;
    default: return a.description || a.type;
  }
}
// A small lucide glyph per action type (shadcn icon idiom, theme-aware).
function ActionIcon({ a, className }) {
  const map = {
    navigate_to: ArrowRight, open_patient: User, apply_protocol: Puzzle,
    create_task: Check, send_message: Mail, book_appointment: CalendarDays, star_favorite: Star,
    attach_photo: Camera,
  };
  const I = map[a.type] || Plus;
  return <I className={className} />;
}

/**
 * Props: pageContext = { screen, path, world, patientId?, patientName?, encounterId? }.
 * Renders nothing but a portal button + panel. Self-sufficient: it resolves the
 * logged-in session + user itself (getMedplum / deriveCurrentUser).
 */
export function GlobalAgentButton({
  pageContext = {}, surfaceActions = [], surfaceContext = null,
  escalations = [], actionContext = null, className = '',
}) {
  const routeSessionKey = routeAgentSessionKey(pageContext);
  const [open, setOpen] = useRouteAgentField(routeSessionKey, 'open', false);
  // Default to the lower-right, clear of WorldSwitcher (bottom:62/right:16) and the
  // ward dock (bottom:18/right:18). window.innerWidth can be 0 at the first render
  // in some engines, so a mount effect below re-clamps into the viewport.
  const [pos, setPos] = React.useState(() => loadPos() || { x: (window.innerWidth || 1024) - 78, y: Math.max(120, (window.innerHeight || 768) - 220) });
  const [dragging, setDragging] = React.useState(false);
  const [text, setText] = useRouteAgentField(routeSessionKey, 'text', '');
  const [interim, setInterim] = React.useState('');
  const [listening, setListening] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = useRouteAgentField(routeSessionKey, 'err', '');
  const [note, setNote] = useRouteAgentField(routeSessionKey, 'note', '');
  const [thread, setThread] = useRouteAgentField(routeSessionKey, 'thread', []);      // [{ id, role, text }]
  const [staged, setStaged] = useRouteAgentField(routeSessionKey, 'staged', []);      // validated proposed actions
  const [applying, setApplying] = React.useState(false);
  const [photo, setPhoto] = useRouteAgentField(routeSessionKey, 'photo', null);      // { file, dataUrl } staged for attach_photo

  const photoInputRef = React.useRef(null);
  const sessionRef = React.useRef(null);
  const stoppingRef = React.useRef(false);
  const dragRef = React.useRef({ moved: false });
  const onRight = pos.x > window.innerWidth / 2;

  const pushTurn = React.useCallback((role, txt) => {
    const t = String(txt || '').trim();
    if (!t) return;
    setThread((prev) => [...prev, { id: nextRouteAgentTurnId(), role, text: t }].slice(-14));
  }, []);

  // Clamp the button into the viewport on mount + on resize — guards against a
  // first render where window.innerWidth was 0 (off-screen), and a viewport that
  // shrinks below a stored position. Snaps to the nearest side, staying clear of
  // the bottom-right dock/switcher band.
  React.useEffect(() => {
    const clamp = () => setPos((p) => {
      const W = window.innerWidth || 1024, H = window.innerHeight || 768;
      const x = Math.max(8, Math.min(W - 60, p.x < 8 || p.x > W - 20 ? W - 78 : p.x));
      const y = Math.max(16, Math.min(H - 60, p.y < 8 || p.y > H - 20 ? Math.max(120, H - 220) : p.y));
      return (x === p.x && y === p.y) ? p : { x, y };
    });
    clamp();
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, []);

  // ── Fluid draggable button (verbatim grammar from CommandAgent) ─────────────
  function onPointerDown(e) {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const base = { ...pos };
    dragRef.current.moved = false;
    setDragging(true);
    const move = (ev) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragRef.current.moved = true;
      setPos({ x: Math.max(8, Math.min(window.innerWidth - 60, base.x + dx)), y: Math.max(8, Math.min(window.innerHeight - 60, base.y + dy)) });
    };
    const up = (ev) => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      setDragging(false);
      if (dragRef.current.moved) {
        const snapped = { x: (ev.clientX < window.innerWidth / 2) ? 16 : window.innerWidth - 68, y: Math.max(16, Math.min(window.innerHeight - 68, base.y + (ev.clientY - startY))) };
        setPos(snapped); savePos(snapped);
      } else {
        if (!open) { setOpen(true); } else if (listening) { stopVoice({ submit: true }); } else { setOpen(false); }
      }
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }

  // ── Voice (window.OPDVoice / Deepgram, Hinglish) — reused from CommandAgent ──
  const voiceAvailable = typeof window !== 'undefined' && window.OPDVoice && typeof window.OPDVoice.create === 'function';
  async function startVoice() {
    if (!voiceAvailable || sessionRef.current) return;
    stoppingRef.current = false;
    try {
      const session = window.OPDVoice.create({
        sessionType: 'opd-intake', speaker: 'Doctor',
        onInterim: (t) => { if (!stoppingRef.current) setInterim(t || ''); },
        onFinal: (t) => { if (stoppingRef.current) return; setText((prev) => (prev ? prev + ' ' : '') + (t || '').trim()); setInterim(''); },
        onError: (error) => setNote(`Mic error: ${error?.message || 'Speech transcription failed'}`),
      });
      sessionRef.current = session;
      const started = await session.start();
      if (started === false) {
        sessionRef.current = null;
        setListening(false);
        return;
      }
      setListening(true);
    } catch (e) { sessionRef.current = null; setNote(`Mic error: ${e.message}`); setListening(false); }
  }
  async function stopVoice({ submit = false } = {}) {
    if (!sessionRef.current && !listening) return;
    stoppingRef.current = true;
    setListening(false); setInterim('');
    let finalText = text;
    try {
      const r = await sessionRef.current?.stop();
      if (r?.transcriptionError) {
        setNote(`Mic error: ${r.transcriptionError.message || 'Speech transcription failed'}`);
        sessionRef.current = null;
        setText(finalText);
        return false;
      }
      if (r && r.transcript && r.transcript.trim()) finalText = r.transcript.trim();
    } catch (error) {
      setNote(`Mic error: ${error?.message || 'Speech transcription failed'}`);
      sessionRef.current = null;
      setText(finalText);
      return false;
    }
    sessionRef.current = null;
    if (submit && finalText.trim()) { setText(''); await ask(finalText); }
    else setText(finalText);
    return true;
  }
  // Stop the mic if the panel closes / component unmounts.
  React.useEffect(() => { if (!open) stopVoice(); return () => { try { sessionRef.current?.stop?.(true); } catch (_) {} }; }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ask the global agent ─────────────────────────────────────────────────
  async function ask(message) {
    const m = String(message || '').trim();
    if (!m) { setNote('Say or type what you want to do.'); return; }
    setBusy(true); setErr(''); setNote('');
    pushTurn('user', m);
    setText('');
    try {
      const { medplum } = await getMedplum();
      const user = await deriveCurrentUser(medplum).catch(() => null);
      // Protocol digest + favourites are best-effort grounding — degrade to null
      // if agent-context.js / its Basic is absent (parallel build). Never blocks.
      let contextDigest = null, favorites = null;
      try {
        const ctxMod = await import('./agent-context.js');
        const digest = await ctxMod.getAgentContextDigest(medplum).catch(() => null);
        if (digest && Array.isArray(digest.protocols) && digest.protocols.length) {
          contextDigest = digest.protocols
            .map((p) => `• ${p.title || p.name || p.id}${p.canonical || p.url ? ` (url ${p.canonical || p.url})` : ''}${p.id ? ` [id ${p.id}]` : ''}`)
            .join('\n');
        }
        if (user && user.id) {
          const favs = await ctxMod.getMyFavorites(medplum, user.id).catch(() => null);
          if (favs) favorites = [...(favs.favouriteMeds || []), ...(favs.favouriteTests || [])].map((f) => (f && f.name) || f).filter(Boolean);
        }
      } catch (_) { /* agent-context not present yet — omit the grounding section */ }

      const priorMessages = thread.map((t) => ({ role: t.role, content: t.text })).concat({ role: 'user', content: m });
      // Let the agent know a photo is staged (+ what vision saw) so it can
      // propose attach_photo with an informed note/description.
      const ctxForAsk = {
        ...pageContext,
        photoStaged: !!photo,
        photoUnderstanding: (photo && photo.understanding)
          ? `${photo.understanding.summary || photo.understanding.kind}${fieldLines(photo.understanding.fields).length ? ` (${fieldLines(photo.understanding.fields).join('; ')})` : ''}`
          : null,
      };
      // taskLedger: tasks confirmed in this route session (shared with the rail
      // CaptureDock via the same session key) — grounds the brain's duplicate
      // judgement alongside the patient's live open Tasks (owner 2026-07-14).
      const r = await askGlobalAgent({
        medplum, user, pageContext: ctxForAsk, messages: priorMessages,
        contextDigest, favorites, surfaceActions, surfaceContext, escalations,
        taskLedger: readAgentSessionTasks(routeSessionKey),
      });
      if (r.reply) pushTurn('assistant', r.reply);
      if (r.taskContextWarning) setNote(r.taskContextWarning);
      if (Array.isArray(r.proposedActions) && r.proposedActions.length) {
        // Merge fresh proposals into the staged set (dedupe by id).
        setStaged((prev) => {
          const seen = new Set(prev.map((a) => a.id));
          return [...prev, ...r.proposedActions.filter((a) => !seen.has(a.id))];
        });
      }
    } catch (e) {
      // Same honest fail-loud message as Spaces.jsx when $ai is not enabled.
      const raw = (e && e.message) || '';
      setErr(/forbidden|feature/i.test(raw)
        ? 'The assistant isn’t enabled on this project yet — an admin must turn on the Medplum “ai” feature and set the OPENAI_API_KEY project secret.'
        : /api key|openai/i.test(raw)
          ? 'The assistant isn’t configured yet — the Medplum project needs its OPENAI_API_KEY secret set.'
          : raw || 'Could not reach the assistant. Check your connection and try again.');
    } finally { setBusy(false); }
  }

  function removeStaged(id) { setStaged((prev) => prev.filter((a) => a.id !== id)); }

  // Paperclip → stage a photo (capped to ≤1280px JPEG) for an attach_photo action,
  // then run the CONTEXT LAYER (understandPhoto — the bearer-gated Cerebras vision
  // leg). Honest degrade: if vision is unreachable, the chip says so and the flow
  // continues on the user's words alone.
  async function onPickPhoto(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setErr('');
    try {
      const { file, dataUrl } = await resizeImageFile(f);
      setPhoto({ file, dataUrl, vision: 'reading', understanding: null });
      setNote('Photo staged — tell me which patient to attach it to.');
      understandPhoto(dataUrl).then((u) => {
        setPhoto((p) => (p && p.dataUrl === dataUrl ? { ...p, vision: 'ready', understanding: u } : p));
      }).catch((err3) => {
        setPhoto((p) => (p && p.dataUrl === dataUrl ? { ...p, vision: 'unavailable', visionMsg: (err3 && err3.message) || 'vision unavailable' } : p));
      });
    } catch (err2) { setErr((err2 && err2.message) || 'Could not read that image.'); }
  }

  // ── Confirm — run every kept action, as the user ───────────────────────────
  async function confirmAll() {
    if (!staged.length || applying) return;
    if (!routeAgentFieldIsFresh(routeSessionKey, 'staged')) {
      setStaged([]);
      setErr('These proposed actions expired. Ask again so you can review current patient data before confirming.');
      return;
    }
    setApplying(true); setErr(''); setNote('');
    let ok = 0; const failed = [];
    let navigated = false;
    try {
      const { medplum } = await getMedplum();
      const user = await deriveCurrentUser(medplum).catch(() => null);
      const meId = user && user.id;
      // The staged photo bytes + the shared write helper are injected here — the
      // agent brain never carries the image, only the resolved patient.
      // Capture-surface context for any attach_photo action — mirrors the
      // QuickPhoto `context` prop so an agent-attached photo carries the same
      // where/who bag. Surface follows the world the agent is invoked in; the
      // per-patient name is filled from the resolved action inside executeAction.
      const photoContext = {
        surface: pageContext.world === 'ipd' ? 'ipd-round' : pageContext.world === 'emergency' ? 'emergency' : 'opd-consult',
        role: (user && user.role) || 'staff',
      };
      const execCtx = {
        meId, user, surfaceActions, actionContext,
        attachPhoto: attachWardPhoto, photoFile: photo && photo.file,
        photoUnderstanding: photo && photo.understanding, photoContext,
      };
      let attachedOk = false;
      // Exact-repeat backstop (owner anti-duplicate rule, 2026-07-14 restoration)
      // — same semantics as CaptureDock.confirmAll: a create_task whose full
      // signature (title+patient+due+note+owner+priority) was already confirmed
      // this session points at the real FHIR Task instead of creating a twin;
      // anything subtler is the LLM's grounded call.
      const sessionTasks = readAgentSessionTasks(routeSessionKey);
      const sessionBySig = new Map(sessionTasks.filter((t) => t && t.sig).map((t) => [t.sig, t]));
      for (const a of staged) {
        try {
          const sig = taskActionSignature(a);
          const existingTask = sig ? sessionBySig.get(sig) : null;
          if (existingTask) {
            ok += 1; // handled, not failed — the work already exists
            const reference = existingTask.reference || (existingTask.taskId ? `Task/${existingTask.taskId}` : null);
            setNote(`Already recorded this session — "${a.title}" points at ${reference || 'the existing task'}.`);
            continue;
          }
          const res = await executeAction(medplum, a, execCtx);
          ok += 1;
          if (a.type === 'create_task' && sig) {
            const taskId = res && res.id ? String(res.id) : null;
            // `sig` stored ON the entry — the ledger's display shape (due, note)
            // differs from the action shape (dueDate); see CaptureDock.confirmAll.
            const ledgerEntry = {
              sig, title: a.title, patientId: a.patientId || null,
              patientName: pageContext.patientName || null,
              due: a.dueDate || null, note: a.note || null,
              ownerId: a.ownerId || null,
              ownerReference: a.ownerId ? `Practitioner/${a.ownerId}` : null,
              priority: a.priority || 'routine',
              taskId,
              reference: taskId ? `Task/${taskId}` : null,
            };
            recordAgentSessionTask(routeSessionKey, ledgerEntry);
            sessionBySig.set(sig, ledgerEntry);
          }
          if (a.type === 'attach_photo') attachedOk = true;
          if (a.type === 'navigate_to' || a.type === 'open_patient') navigated = true;
          if (res && res.note) setNote(res.note);
        } catch (e) {
          // Surface the READABLE reason, never a bare status code (AGENTS §7 —
          // e.g. the apply route's department-ownership 403 carries a human
          // sentence; a body-less refusal gets a plain access line instead).
          const raw = (e && e.message) ? String(e.message).trim() : '';
          const human = raw && !/^\d{3}$/.test(raw) ? raw
            : e && e.status === 403 ? 'you do not have permission for this'
              : 'something went wrong';
          failed.push({ action: a, message: `${actionLabel(a)} — ${human}` });
        }
      }
    } catch (e) { setErr((e && e.message) || 'Could not run the actions.'); setApplying(false); return; }
    // Keep failed structured actions (including resolved UUIDs) so the user can
    // retry exactly what they confirmed after correcting the underlying error.
    setStaged(failed.map((f) => f.action));
    // Consume the staged photo once it's been attached (don't re-attach it next turn).
    if (attachedOk) setPhoto(null);
    if (ok) pushTurn('assistant', `Done — ran ${ok} action${ok === 1 ? '' : 's'}${failed.length ? `; ${failed.length} failed` : ''}.`);
    if (failed.length) {
      const detail = failed.map((f) => f.message).join('; ');
      setErr(`Couldn’t run: ${detail}`); pushTurn('assistant', `Couldn’t run: ${detail}`);
    }
    setApplying(false);
    // A navigation closes the panel (we're leaving the screen anyway).
    if (navigated && !failed.length) setOpen(false);
  }

  const PANEL_W = 440;
  const panelLeft = onRight ? Math.max(12, pos.x - PANEL_W - 12) : Math.min(window.innerWidth - PANEL_W - 12, pos.x + 64);
  const panelTop = Math.max(12, Math.min(pos.y - 150, window.innerHeight - 600));
  const originX = onRight ? '100%' : '0%';

  return createPortal(
    <>
      <style>{`
        @keyframes ga-pop { from { opacity: 0; transform: scale(.8) translateY(12px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes ga-chip { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ga-ring { 0% { box-shadow: 0 0 0 0 hsl(var(--recording)/.45); } 70% { box-shadow: 0 0 0 13px hsl(var(--recording)/0); } 100% { box-shadow: 0 0 0 0 hsl(var(--recording)/0); } }
        @keyframes ga-dot { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
      `}</style>

      {/* Fluid draggable FAB — primary (world-aware: ward indigo in IPD), recording red while listening */}
      <button
        type="button" data-opd-skip-route="true" onPointerDown={onPointerDown} title="Assistant — ask to do anything (drag to move)"
        className={cn('flex items-center justify-center text-primary-foreground', className)}
        style={{
          position: 'fixed', left: pos.x, top: pos.y, width: 52, height: 52, borderRadius: '50%',
          border: 'none', cursor: dragging ? 'grabbing' : 'grab', zIndex: 9500, touchAction: 'none',
          background: listening ? 'hsl(var(--recording))' : 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))', boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
          transition: dragging ? 'none' : 'left .42s cubic-bezier(.34,1.4,.64,1), top .42s cubic-bezier(.34,1.4,.64,1), background .2s, transform .12s',
          transform: open ? 'scale(.92)' : 'scale(1)',
          animation: listening ? 'ga-ring 1.4s infinite' : 'none',
        }}>
        {listening ? <Mic className="size-5" /> : <Sparkles className="size-5" />}
      </button>

      {open && createPortal(
        <Card
          data-opd-skip-route="true"
          data-shell="v2"
          className={cn('flex flex-col gap-0 overflow-hidden p-0 shadow-2xl', className)}
          style={{
            position: 'fixed', left: panelLeft, top: panelTop, width: PANEL_W, maxHeight: '80vh', zIndex: 9501,
            transformOrigin: `${originX} 60%`, animation: 'ga-pop .22s cubic-bezier(.34,1.4,.64,1)',
          }}>
          {/* Header */}
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <span
              className="size-2 flex-none rounded-full"
              style={{
                background: listening ? 'hsl(var(--recording))' : (busy || applying ? 'hsl(var(--ai-foreground))' : 'hsl(var(--primary))'),
                animation: (listening || busy || applying) ? 'ga-dot 1.1s infinite' : 'none',
              }} />
            <Sparkles className="size-4 text-primary" />
            <span className="text-[13px] font-semibold text-foreground">Assistant</span>
            <span className="truncate text-[11px] text-muted-foreground">{busy ? 'Thinking…' : applying ? 'Running…' : (pageContext.screen || '')}</span>
            <span className="flex-1" />
            {/* Close — the registry dialog/close idiom exactly: a ghost icon
                Button, muted-foreground, lucide X (no dark filled circle). */}
            <Button type="button" data-opd-skip-route="true" variant="ghost" size="icon" aria-label="Close assistant" className="text-muted-foreground" onClick={() => setOpen(false)}>
              <X className="size-4" />
            </Button>
          </div>

          <div className="flex min-h-0 flex-col gap-3 p-4">
            {/* Conversation thread */}
            {thread.length > 0 && (
              <div className="flex min-h-0 flex-col">
                <div className="mb-1.5 flex items-center">
                  <span className="text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Conversation</span>
                  <span className="flex-1" />
                  <Button type="button" data-opd-skip-route="true" variant="ghost" size="sm"
                    onClick={() => clearRouteAgentSession(routeSessionKey)} title="Clear"
                    className="h-6 px-2 text-[10px] font-semibold text-muted-foreground">Clear</Button>
                </div>
                {/* Thread on the real message/message-scroller anatomy: each turn is
                    a Message + Bubble (primary bubble for the user, muted for the
                    assistant), and MessageScroller keeps the newest turn pinned. */}
                <MessageScrollerProvider>
                  <MessageScroller className="max-h-[300px]">
                    <MessageScrollerViewport className="pr-1">
                      <MessageScrollerContent className="gap-1.5">
                        {thread.map((t, i) => (
                          <MessageScrollerItem key={t.id} scrollAnchor={i === thread.length - 1}>
                            <Message align={t.role === 'user' ? 'end' : 'start'}>
                              <MessageContent>
                                <Bubble variant={t.role === 'user' ? 'default' : 'muted'} align={t.role === 'user' ? 'end' : 'start'}>
                                  <BubbleContent className="whitespace-pre-wrap text-[12px] leading-relaxed">{t.text}</BubbleContent>
                                </Bubble>
                              </MessageContent>
                            </Message>
                          </MessageScrollerItem>
                        ))}
                      </MessageScrollerContent>
                    </MessageScrollerViewport>
                    <MessageScrollerButton direction="end" />
                  </MessageScroller>
                </MessageScrollerProvider>
              </div>
            )}

            {/* One command box — voice fills it, type/edit too. ↵ = ask. Built on
                the shadcn input-group anatomy: a textarea with a block-end addon
                holding the mic (outline/destructive icon), the live interim
                transcript, and the primary Ask button (Spinner while thinking). */}
            {/* Staged photo chip — a thumbnail the agent will attach on Confirm */}
            {photo && (
              <div className="flex items-center gap-2 rounded-[9px] border border-border bg-muted/60 px-2 py-1.5" style={{ animation: 'ga-chip .18s ease' }}>
                <img src={photo.dataUrl} alt="Staged photo" className="rounded" style={{ width: 34, height: 34, objectFit: 'cover', flex: 'none' }} />
                <span className="min-w-0 flex-1 text-[12px] text-foreground" style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="truncate">Photo ready — say which patient to attach it to</span>
                  <span className="truncate text-[11px] text-muted-foreground">
                    {photo.vision === 'reading' ? 'Reading photo…'
                      : photo.vision === 'ready' && photo.understanding ? `Looks like: ${photo.understanding.summary || photo.understanding.kind}`
                        : photo.vision === 'unavailable' ? (photo.visionMsg || 'Vision unavailable') : ''}
                  </span>
                </span>
                <Button type="button" data-opd-skip-route="true" variant="ghost" size="icon" title="Remove photo"
                  onClick={() => setPhoto(null)} className="size-6 flex-none text-muted-foreground"><X className="size-3.5" /></Button>
              </div>
            )}
            {/* No `capture` attr: the OS chooser (camera OR photo library) opens so
                the user can take a photo OR choose one from the gallery. */}
            <input ref={photoInputRef} type="file" accept="image/*" onChange={onPickPhoto} style={{ display: 'none' }} />
            <InputGroup>
              <InputGroupTextarea
                value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && text.trim()) { e.preventDefault(); ask(text); } }}
                rows={2}
                placeholder='Ask to do anything… e.g. "take me to the queue", "apply the diabetic review to Asha", "create a follow-up task for Ramesh Friday"'
                className="text-[12.5px]" />
              <InputGroupAddon align="block-end">
                <InputGroupButton
                  type="button" data-opd-skip-route="true" variant={photo ? 'default' : 'outline'} size="icon-xs"
                  disabled={busy || applying}
                  onClick={() => photoInputRef.current?.click()}
                  title={photo ? 'Photo staged — click to replace' : 'Attach a photo'}
                  aria-label="Attach a photo">
                  <Paperclip />
                </InputGroupButton>
                <InputGroupButton
                  type="button" data-opd-skip-route="true" variant={listening ? 'destructive' : 'outline'} size="icon-xs"
                  disabled={!voiceAvailable || busy || applying}
                  onClick={() => (listening ? stopVoice({ submit: true }) : startVoice())}
                  title={voiceAvailable ? (listening ? 'Stop & ask' : 'Speak') : 'Voice unavailable here'}
                  aria-label={listening ? 'Stop & ask' : 'Speak'}>
                  {listening ? <Square /> : <Mic />}
                </InputGroupButton>
                {listening && interim
                  ? <InputGroupText className="min-w-0 flex-1 truncate italic">{interim}…</InputGroupText>
                  : <span className="flex-1" />}
                {/* Ask CTA — the default (black/primary) Button with a Kbd ⏎
                    hint (registry kbd component), not a bordered "boxed" button. */}
                <InputGroupButton
                  type="button" data-opd-skip-route="true" variant="default" size="sm" className="gap-1.5"
                  disabled={busy || applying || !text.trim()} onClick={() => ask(text)}>
                  {busy ? <><Spinner /> Thinking…</> : <>Ask <Kbd className="bg-background/20 text-primary-foreground">⏎</Kbd></>}
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>

            {/* Fail-loud error (e.g. $ai not enabled) */}
            {err && <div className="text-[11.5px] leading-relaxed text-destructive">{err}</div>}

            {/* Staged proposed actions — each removable; Confirm runs the kept ones */}
            {staged.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <div className="text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Proposed — confirm to run</div>
                {staged.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 rounded-[9px] border border-border bg-muted/60 px-2.5 py-1.5 text-[12px] text-foreground"
                    style={{ animation: 'ga-chip .18s ease' }}>
                    <ActionIcon a={a} className="size-3.5 flex-none text-primary" />
                    <span className="min-w-0 flex-1 truncate">{actionLabel(a)}</span>
                    <Button type="button" data-opd-skip-route="true" variant="ghost" size="icon" title="Remove"
                      onClick={() => removeStaged(a.id)}
                      className="size-6 flex-none text-muted-foreground"><X className="size-3.5" /></Button>
                  </div>
                ))}
                <Button type="button" data-opd-skip-route="true" className="mt-1 h-10 w-full" disabled={applying} onClick={confirmAll}>
                  {applying ? 'Running…' : `Confirm ${staged.length} action${staged.length === 1 ? '' : 's'}`}
                </Button>
              </div>
            )}

            {note && <div className="text-[11.5px] text-muted-foreground">{note}</div>}
            {!voiceAvailable && <div className="text-[10.5px] text-muted-foreground">Voice unavailable here — type your request.</div>}
          </div>
        </Card>, document.body)}
    </>,
    document.body,
  );
}

export default GlobalAgentButton;
